"use client";

import { format } from "date-fns";
import { motion } from "framer-motion";
import { Calendar, Newspaper } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import type { ArticleSummary } from "@/lib/shopify/types";

interface RelatedBlogsProps {
	articles: ArticleSummary[];
	currentArticleId: string;
	title?: string;
}

const container = {
	hidden: { opacity: 0 },
	show: {
		opacity: 1,
		transition: {
			staggerChildren: 0.1,
			delayChildren: 0.1,
		},
	},
};

const item = {
	hidden: { opacity: 0, y: 30 },
	show: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.5, ease: "easeOut" as const },
	},
};

export function RelatedBlogs({
	articles,
	currentArticleId,
	title = "Related Articles",
}: RelatedBlogsProps) {
	const filtered = articles
		.filter((a) => a.id !== currentArticleId)
		.slice(0, 3);

	if (filtered.length === 0) return null;

	return (
		<section className="pt-0">
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, margin: "-100px" }}
				transition={{ duration: 0.5 }}
			>
				<h2 className="text-2xl font-bold mb-6">{title}</h2>
			</motion.div>

			<motion.div
				variants={container}
				initial="hidden"
				whileInView="show"
				viewport={{ once: true, margin: "-50px" }}
				className="grid grid-cols-1 md:grid-cols-3 gap-6"
			>
				{filtered.map((article) => (
					<motion.div key={article.id} variants={item}>
						<Link
							href={`/blog/${article.handle}`}
							className="block group h-full"
						>
							<Card className="h-full overflow-hidden transition-all hover:shadow-lg hover:border-primary/50 hover:-translate-y-1 duration-300 border shadow-sm p-0 gap-0">
								<div className="relative aspect-video overflow-hidden">
									{article.image ? (
										<Image
											src={article.image.url}
											alt={article.image.altText || article.title}
											fill
											className="object-cover transition-transform duration-500 group-hover:scale-105"
											sizes="(max-width: 768px) 100vw, 33vw"
										/>
									) : (
										<div className="h-full bg-secondary flex items-center justify-center">
											<Newspaper className="h-10 w-10 text-muted-foreground" />
										</div>
									)}
								</div>
								<CardContent className="p-5">
									{article.blog?.title && (
										<Badge variant="secondary" className="mb-2 text-xs">
											{article.blog.title}
										</Badge>
									)}
									<h3 className="font-semibold text-base group-hover:text-primary transition-colors line-clamp-2 min-h-11">
										{article.title}
									</h3>
									{article.excerpt && (
										<p className="text-sm text-muted-foreground mt-2 line-clamp-2 min-h-10">
											{article.excerpt}
										</p>
									)}
									<div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
										<Calendar className="h-3.5 w-3.5" />
										<span>
											{format(new Date(article.publishedAt), "MMM d, yyyy")}
										</span>
									</div>
								</CardContent>
							</Card>
						</Link>
					</motion.div>
				))}
			</motion.div>
		</section>
	);
}
